import { ScoreTile } from "apps/company/api/rest/company-dashboard-resilience";

// The nine domains in the order the page lists them; the payload keys them
// the same way.
export const DOMAINS = [
  "movement",
  "nutrition",
  "personal_stress",
  "mind",
  "sleep",
  "social",
  "work_stress",
  "job_outcomes",
  "work_environment",
] as const;

export type Domain = typeof DOMAINS[number];

// Each domain's themes, from the scoring hierarchy. Nutrition has none: the
// page never shows its themes, since with the domain they give Alcohol away.
export const DOMAIN_THEMES: Record<Domain, readonly string[]> = {
  movement: ["active_minutes"],
  nutrition: [],
  personal_stress: ["personal_stress"],
  mind: [
    "emotion_regulation",
    "coping_mindset",
    "negative_thinking_loops",
    "purpose_gratitude",
  ],
  sleep: ["sleep_quality", "energy", "sleep_duration", "sleep_efficiency"],
  social: ["compassion_contribution", "connection", "support"],
  work_stress: ["burnout", "work_stress"],
  job_outcomes: ["performance", "retention"],
  work_environment: [
    "work_psychological_safety",
    "work_relationships",
    "work_role",
  ],
};

// The groupings a company's people are split by, in the order the page offers
// them.
export const DIMENSIONS = ["location", "business_unit", "manager"] as const;

export type Dimension = typeof DIMENSIONS[number];

// Score bands from the Index's psychometric cutoffs.
export type Band = "steady" | "watching" | "nurturing";

// The lower bound of each of the learner app's score labels, mirrored from
// wone_index/score_labels.py so the dashboard reads the same as the app.
const STRONG = 75;
const STEADY = 63;
const WORTH_WATCHING = 48;
const NEEDS_NURTURING = 25;

export const band = (score: number): Band => {
  if (score >= STEADY) return "steady";
  if (score >= WORTH_WATCHING) return "watching";
  return "nurturing";
};

export type SentenceBand =
  | "strong"
  | "steady"
  | "watching"
  | "nurturing"
  | "restoration";

export const sentenceBand = (score: number): SentenceBand => {
  if (score >= STRONG) return "strong";
  if (score >= STEADY) return "steady";
  if (score >= WORTH_WATCHING) return "watching";
  if (score >= NEEDS_NURTURING) return "nurturing";
  return "restoration";
};

export type Trend = "rising" | "steady" | "falling";

// A change of under two points either way reads as steady.
const TREND_POINTS = 2;

export const trend = (change: number): Trend => {
  if (change >= TREND_POINTS) return "rising";
  if (change <= -TREND_POINTS) return "falling";
  return "steady";
};

const HALF = /^(\d{4})-H([12])$/;

export const halfParts = (value: string) => {
  const match = HALF.exec(value);
  if (match === null) {
    throw new Error(`Not a half-year: ${value}`);
  }
  return { year: Number(match[1]), number: Number(match[2]) };
};

export const previousHalf = (value: string) => {
  const { year, number } = halfParts(value);
  return number === 2 ? `${year}-H1` : `${year - 1}-H2`;
};

export const nextHalf = (value: string) => {
  const { year, number } = halfParts(value);
  return number === 1 ? `${year}-H2` : `${year + 1}-H1`;
};

// The last day of a half-year, as a UTC date.
export const halfEnd = (year: number, number: number) =>
  number === 1
    ? new Date(Date.UTC(year, 5, 30))
    : new Date(Date.UTC(year, 11, 31));

// All staff's scores come to one decimal place; a whole one keeps its ".0"
// so a column of them lines up.
export const formatScore = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);

// Groups' scores come as whole numbers.
export const formatGroupScore = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);

// A size of change with no sign, for copy that already says which way.
export const formatPoints = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(
    Math.abs(value)
  );

// The trend's points as the chart plots them: a hidden half is a gap.
export const trendValues = (points: { index: ScoreTile }[]) =>
  points.map((point) => (point.index.suppressed ? null : point.index.value));

export const formatChange = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, {
    maximumFractionDigits: 1,
    signDisplay: "exceptZero",
  }).format(value);

export const formatCount = (value: number, locale: string) =>
  new Intl.NumberFormat(locale).format(value);

// A y-axis for the trend that zooms in on the plotted scores: five points
// either side of them, snapped outward to a round step so there are at most
// five ticks, and never past the 0 to 100 scale.
export const trendAxis = (values: number[]) => {
  const low = Math.max(0, Math.floor(Math.min(...values) - 5));
  const high = Math.min(100, Math.ceil(Math.max(...values) + 5));
  for (const step of [1, 2, 5, 10, 20, 25, 50]) {
    const bottom = Math.floor(low / step) * step;
    const top = Math.min(100, Math.ceil(high / step) * step);
    if ((top - bottom) / step <= 4) {
      const ticks: number[] = [];
      for (let tick = bottom; tick <= top; tick += step) ticks.push(tick);
      return { domain: [bottom, top] as [number, number], ticks };
    }
  }
  return { domain: [0, 100] as [number, number], ticks: [0, 50, 100] };
};
